import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import {
  ChevronDown,
  Circle,
  CheckCircle,
  MinusCircle,
  School,
} from "lucide-react";
import { BaseStudentResponseDto } from "@packages/api/madlenAPI.schemas";
import {
  getClassroomInfoAsTeacherForAssignViewPage,
  getGetClassroomInfoAsTeacherForAssignViewPageQueryKey,
  useGetLoggedInTeacher,
} from "@packages/api/madlenAPI";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CreateClassroomModal } from "../../../students/components/CreateClassroomModal";
import AddStudentModal from "../../../students/components/AddStudentModal";
import { getCurrentTeacher } from "@packages/store/userSlice";
import { Button } from "@core/button";
import { useNavigate } from "react-router-dom";
import { Text } from "@core/text";
import { CourseData } from "@packages/constant/CourseData";

interface CourseAndStudentSelectorProps {
  selectedStudents: {
    classroomId: number;
    studentIds: number[];
  }[];
  setSelectedStudents: (
    students: {
      classroomId: number;
      studentIds: number[];
    }[],
  ) => void;
  shouldUseModal?: boolean;
  assignedStudentIds?: number[];
  scrollToBottom?: () => void;
}

export const ClassroomAndStudentSelector = ({
  selectedStudents,
  setSelectedStudents,
  shouldUseModal = true,
  assignedStudentIds = [],
  scrollToBottom,
}: CourseAndStudentSelectorProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showCreateClassroomModal, setShowCreateClassroomModal] =
    useState(false);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [selectedClassUUID, setSelectedClassUUID] = useState<string>();
  const isEdlinkUser = useAppSelector(
    (state) => !!state.user.teacher?.edlinkPersonId,
  );

  const dispatch = useAppDispatch();
  // const teacherClasses = useAppSelector(
  //   (state) => state.user.teacher?.classes ?? [],
  // );
  const { data: teacher, isLoading } = useGetLoggedInTeacher();
  const teacherClasses = teacher?.classes ?? [];
  const queryClient = useQueryClient();

  const allClassrooms = useQueries({
    queries: teacherClasses
      .filter((c) => c.studentCount > 0)
      .map((classroom) => ({
        queryKey: getGetClassroomInfoAsTeacherForAssignViewPageQueryKey(
          classroom.id,
        ),
        queryFn: () => getClassroomInfoAsTeacherForAssignViewPage(classroom.id),
      })),
  });

  const allStudents = useMemo(
    () =>
      allClassrooms.reduce(
        (acc, query) => {
          return {
            ...acc,
            [query.data?.id ?? 0]: query.data?.students ?? [],
          };
        },
        {} as Record<number, BaseStudentResponseDto[]>,
      ),
    [allClassrooms],
  );

  const [searchTerm, setSearchTerm] = useState("");

  const generateClassroomLabel = useCallback(
    (classroomId: number) => {
      const classroom = allClassrooms.find((c) => c.data?.id === classroomId);
      return `${classroom?.data?.name} (${classroom?.data?.grade} - ${t(
        "courseType." +
          CourseData[
            classroom?.data?.courseType as keyof typeof CourseData
          ]?.name(classroom?.data?.grade ?? 8),
      )})`;
    },
    [allClassrooms],
  );

  const allStudentsAsOption = useMemo(() => {
    return Object.entries(allStudents)
      .map(([classroomId, students]) => ({
        label: generateClassroomLabel(parseInt(classroomId)),
        options: students
          .map((student) => ({
            id: student.id,
            label: student.name,
            disabled: assignedStudentIds.includes(student.id),
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        classroomId: parseInt(classroomId),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allStudents, assignedStudentIds]);

  const filteredStudents = useMemo(() => {
    if (!searchTerm) return allStudentsAsOption;

    const lowercaseSearch = searchTerm.toLowerCase();
    return allStudentsAsOption
      .map((unit) => ({
        ...unit,
        options: unit.options.filter((topic) =>
          topic.label.toLowerCase().includes(lowercaseSearch),
        ),
      }))
      .filter(
        (unit) =>
          unit.label.toLowerCase().includes(lowercaseSearch) ||
          unit.options.length > 0,
      );
  }, [allStudentsAsOption, searchTerm]);

  const [expandedUnit, setExpandedUnit] = useState<string | null>(null);

  useEffect(() => {
    if (filteredStudents.length > 0) {
      setExpandedUnit(
        filteredStudents.find((unit) => unit.options.length > 0)?.label ?? null,
      );
    }
  }, [JSON.stringify(filteredStudents)]);

  useEffect(() => {
    if (
      !shouldUseModal &&
      allClassrooms.length === 0 &&
      teacherClasses.length > 0
    ) {
      setShowCreateClassroomModal(false);
      setShowAddStudentModal(true);
    }
  }, [
    shouldUseModal,
    JSON.stringify(allClassrooms),
    JSON.stringify(teacherClasses),
  ]);

  const isLoadingState =
    allClassrooms.some((query) => query.isLoading) || isLoading;
  const wasLoadingRef = useRef(isLoadingState);

  // When loading finishes, scroll to the bottom of the selector
  useEffect(() => {
    if (wasLoadingRef.current && !isLoadingState && scrollToBottom) {
      scrollToBottom();
    }
    wasLoadingRef.current = isLoadingState;
  }, [isLoadingState, scrollToBottom]);

  if (isLoadingState) {
    return (
      <div className="w-full rounded-lg overflow-hidden">
        <div className="p-4 text-center">
          <div className="w-16 h-16 mx-auto mb-4">
            <svg
              className="w-full h-full text-gray-400 dark:text-gray-600 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <Text variant="h3">common.loading</Text>
          <Text variant="p" status="secondary">
            common.pleaseWait
          </Text>
        </div>
        <div className="px-4 pb-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-2 last:mb-0"
            >
              <div className="flex items-center justify-between animate-pulse">
                <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="flex items-center space-x-3">
                  <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (allClassrooms.length === 0) {
    if (isEdlinkUser) {
      return (
        <div className="text-center border border-primary-500 dark:border-primary-900/20 p-6 bg-primary-50 dark:bg-primary-900/20 rounded-lg mb-4">
          <Text
            variant={"h3"}
            className="!text-primary-900 dark:!text-primary-100 mb-2"
          >
            profile.importClassrooms
          </Text>
          <Text
            size={"sm"}
            className=" !text-primary-700 dark:!text-primary-300 mb-4"
          >
            profile.edlinkConnected
          </Text>
          <Button
            onClick={() => {
              navigate("/profiles/me");
            }}
            size="md"
            rounded="full"
            bgColor="bg-primary-500 hover:bg-primary-600"
            textColor="text-white"
            className="px-4"
          >
            <School className="w-4 h-4 mr-2" />
            {t("profile.importClassrooms")}
          </Button>
        </div>
      );
    }
    return (
      <div className="text-center text-gray-500 dark:text-gray-400">
        <Text variant="h3">
          {t(
            teacherClasses.length > 0
              ? "students.noStudents"
              : "students.noClassrooms",
          )}
        </Text>
        <Button
          rounded="full"
          bgColor={
            showCreateClassroomModal || shouldUseModal
              ? "bg-primary-500 hover:bg-primary-600"
              : "bg-gray-500 hover:bg-gray-600"
          }
          textColor="text-white"
          className="mb-4 mt-2"
          size="md"
          onClick={() => {
            setShowAddStudentModal(false);
            setShowCreateClassroomModal(true);
          }}
        >
          {t("quickActions.createClassroom")}
        </Button>
        <Button
          rounded="full"
          bgColor={
            showAddStudentModal || shouldUseModal
              ? "bg-primary-500 hover:bg-primary-600"
              : "bg-gray-500 hover:bg-gray-600"
          }
          textColor="text-white"
          className="mb-4 ml-3 mt-2"
          size="md"
          onClick={() => {
            setShowCreateClassroomModal(false);
            setShowAddStudentModal(true);
          }}
        >
          {t("quickActions.addStudent")}
        </Button>
        <CreateClassroomModal
          show={showCreateClassroomModal}
          onSubmit={(classroom) => {
            setShowCreateClassroomModal(false);
            setSelectedClassUUID(classroom.uuid);
            setShowAddStudentModal(true);
          }}
          shouldUseModal={shouldUseModal}
          onClose={async () => {
            setShowCreateClassroomModal(false);
            setSelectedClassUUID(undefined);
            await dispatch(getCurrentTeacher());
            teacherClasses.forEach((classroom) => {
              queryClient.invalidateQueries({
                queryKey: getGetClassroomInfoAsTeacherForAssignViewPageQueryKey(
                  classroom.id,
                ),
              });
            });
          }}
        />
        <AddStudentModal
          show={showAddStudentModal}
          shouldUseModal={shouldUseModal}
          onClose={async () => {
            setShowAddStudentModal(false);
            setSelectedClassUUID(undefined);
            await dispatch(getCurrentTeacher());
            teacherClasses.forEach((classroom) => {
              queryClient.invalidateQueries({
                queryKey: getGetClassroomInfoAsTeacherForAssignViewPageQueryKey(
                  classroom.id,
                ),
              });
            });
          }}
          uuid={selectedClassUUID}
        />
      </div>
    );
  }

  const toggleUnit = (unitLabel: string) => {
    setExpandedUnit((prev) => (prev === unitLabel ? null : unitLabel));
  };

  const isStudentSelected = (studentId: number) =>
    selectedStudents.some((student) => student.studentIds.includes(studentId));

  const getUnitSelectionState = (unit: (typeof filteredStudents)[0]) => {
    const selectableStudents = unit.options.filter(
      (student) => !student.disabled,
    );
    const selectedCount = selectableStudents.filter((student) =>
      selectedStudents.some((s) => s.studentIds.includes(student.id)),
    ).length;
    if (selectableStudents.length === 0) return "none";
    if (selectedCount === 0) return "none";
    if (selectedCount === selectableStudents.length) return "all";
    return "some";
  };

  const toggleAllStudentsInUnit = (unit: (typeof filteredStudents)[0]) => {
    const unitState = getUnitSelectionState(unit);
    const selectableStudentIds = unit.options
      .filter((student) => !student.disabled)
      .map((student) => student.id);

    if (unitState === "all") {
      setSelectedStudents(
        selectedStudents.filter((s) => s.classroomId !== unit.classroomId),
      );
    } else {
      setSelectedStudents([
        ...selectedStudents.filter((s) => s.classroomId !== unit.classroomId),
        {
          classroomId: unit.classroomId,
          studentIds: selectableStudentIds,
        },
      ]);
    }
  };

  return (
    <div className="space-y-4 my-8 w-full">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t("common.searchClassroomOrStudent")}
          className="w-full px-4 py-2 border rounded-lg dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      <div className="w-full border rounded-lg overflow-hidden dark:border-gray-700">
        {filteredStudents.map((unit) => {
          const unitState = getUnitSelectionState(unit);
          const hasSelectableStudents = unit.options.some(
            (student) => !student.disabled,
          );

          return (
            <div
              key={unit.classroomId}
              className="border-b last:border-b-0 dark:border-gray-700"
            >
              <div className="flex">
                <Button
                  onClick={() => toggleAllStudentsInUnit(unit)}
                  bgColor="bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                  size="md"
                  disabled={!hasSelectableStudents}
                >
                  <span className="text-sm whitespace-nowrap mr-1">
                    {t("common.selectAll")}
                  </span>
                  {unitState === "none" && (
                    <Circle size={16} className="text-gray-400" />
                  )}
                  {unitState === "some" && (
                    <MinusCircle size={16} className="text-orange-500" />
                  )}
                  {unitState === "all" && (
                    <CheckCircle size={16} className="text-orange-500" />
                  )}
                </Button>
                <Button
                  onClick={() => toggleUnit(unit.label)}
                  bgColor="bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                  className="flex items-center justify-between"
                  fullWidth
                  size="md"
                >
                  <Text variant="p" className="text-left">
                    {unit.label}
                  </Text>
                  <div className="flex items-center space-x-2">
                    <Text variant="p" status="secondary">
                      {
                        unit.options.filter(
                          (student) =>
                            selectedStudents.some((s) =>
                              s.studentIds.includes(student.id),
                            ) || assignedStudentIds.includes(student.id),
                        ).length
                      }
                      /{unit.options.length}
                    </Text>
                    <ChevronDown
                      size={16}
                      className={`text-gray-400 dark:text-gray-500 transition-transform ${expandedUnit === unit.label ? "rotate-180" : ""}`}
                    />
                  </div>
                </Button>
              </div>

              {expandedUnit === unit.label && (
                <div className="relative">
                  <div className="bg-white dark:bg-gray-800 px-4 py-2 max-h-[250px] overflow-y-auto">
                    {unit.options.map((student) => (
                      <Button
                        textColor={
                          student.disabled
                            ? "text-gray-400 dark:text-gray-500"
                            : isStudentSelected(student.id)
                              ? "text-orange-600 dark:text-orange-300"
                              : "text-gray-800 dark:text-gray-200"
                        }
                        bgColor={
                          student.disabled
                            ? "bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
                            : isStudentSelected(student.id)
                              ? "bg-orange-50 dark:bg-orange-900/30"
                              : "hover:bg-gray-100 dark:hover:bg-gray-700"
                        }
                        rounded="lg"
                        key={student.id}
                        fullWidth
                        size="md"
                        className={`my-0.5 text-left focus:outline-none ${student.disabled ? "opacity-50" : ""}`}
                        disabled={student.disabled}
                        onClick={() => {
                          if (student.disabled) return;

                          if (isStudentSelected(student.id)) {
                            const updatedSelections = selectedStudents
                              .map((s) => {
                                if (s.classroomId === unit.classroomId) {
                                  const newStudentIds = s.studentIds.filter(
                                    (id) => id !== student.id,
                                  );
                                  return newStudentIds.length > 0
                                    ? {
                                        classroomId: unit.classroomId,
                                        studentIds: newStudentIds,
                                      }
                                    : null;
                                }
                                return s;
                              })
                              .filter(
                                (
                                  s,
                                ): s is {
                                  classroomId: number;
                                  studentIds: number[];
                                } => s !== null,
                              );
                            setSelectedStudents(updatedSelections);
                          } else {
                            const existingClassroomIndex =
                              selectedStudents.findIndex(
                                (s) => s.classroomId === unit.classroomId,
                              );

                            if (existingClassroomIndex !== -1) {
                              const updatedSelections = [...selectedStudents];
                              updatedSelections[existingClassroomIndex] = {
                                classroomId: unit.classroomId,
                                studentIds: [
                                  ...updatedSelections[existingClassroomIndex]
                                    .studentIds,
                                  student.id,
                                ],
                              };
                              setSelectedStudents(updatedSelections);
                            } else {
                              setSelectedStudents([
                                ...selectedStudents,
                                {
                                  classroomId: unit.classroomId,
                                  studentIds: [student.id],
                                },
                              ]);
                            }
                          }
                        }}
                      >
                        <Text variant="p" size="sm">
                          {student.label}
                        </Text>
                      </Button>
                    ))}
                  </div>
                  {unit.options.length > 5 && (
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-events-none" />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
